import type { ReapplyPolicy } from "@repo/app-types/entities/company";
import { defaultReapplyPolicy } from "@repo/app-types/entities/company";
import {
  PermissionActions,
  PermissionSubjects,
} from "@repo/app-types/entities/teammatePermissions";
import { SaveAndCancelButtons } from "@repo/ui/Buttons/saveAndCancelButtons";
import { Label } from "@repo/ui/Label/label";
import { SimpleSelect } from "@repo/ui/Select/simpleSelect";
import { Size } from "@repo/ui/helpers/helpers";
import { type FC, useState } from "react";
import { Can } from "../../../helpers/teammateAuthorizationContext.js";

const reapplyPolicyOptions = [
  {
    label: "Never (single application only)",
    value: 0,
  },
  { label: "After 7 days", value: 7 },
  { label: "After 15 days", value: 15 },
  { label: "After 30 days", value: 30 },
  { label: "After 60 days", value: 60 },
  { label: "After 90 days", value: 90 },
  { label: "After 180 days", value: 180 },
];

interface CompanyReapplyPolicyEditorProps {
  onSave: (reapplyPolicy: ReapplyPolicy) => void;
  reapplyPolicy: ReapplyPolicy;
}

export const CompanyReapplyPolicyEditor: FC<
  CompanyReapplyPolicyEditorProps
> = ({ onSave, reapplyPolicy: initialReapplyPolicy }) => {
  const [reapplyPolicy, setReapplyPolicy] = useState(
    initialReapplyPolicy || defaultReapplyPolicy,
  );

  return (
    <Can
      I={PermissionActions.UPDATE}
      a={PermissionSubjects.COMPANY_SETTINGS}
      passThrough={true}
    >
      {(allowed) => (
        <>
          <Label size={Size.SMALL} className="mb-2 block">
            Reapply policy
          </Label>
          <div className="w-[280px]">
            <SimpleSelect
              label="Reapply policy"
              isLabelHidden
              options={reapplyPolicyOptions}
              value={
                reapplyPolicy.enabled ? reapplyPolicy.threshold_in_days : 0
              }
              onChange={(value) => {
                // "0" means we want to disable the policy
                setReapplyPolicy({
                  enabled: value === 0 ? false : true,
                  threshold_in_days:
                    value === 0 ? reapplyPolicy.threshold_in_days : value,
                });
              }}
              isDisabled={!allowed}
            />
          </div>
          {(reapplyPolicy.enabled !== initialReapplyPolicy.enabled ||
            reapplyPolicy.threshold_in_days !==
              initialReapplyPolicy.threshold_in_days) && (
            <SaveAndCancelButtons
              onSave={() => {
                onSave(reapplyPolicy);
              }}
              onCancel={() => {
                setReapplyPolicy(initialReapplyPolicy);
              }}
            />
          )}

          <div className="mt-2 min-w-[280px] text-sm text-muted-foreground">
            Whether candidates can reapply to a job opening they've already
            applied to previously.
          </div>
        </>
      )}
    </Can>
  );
};
